import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { AlertCircle, PanelRightOpen } from 'lucide-react'
import { getApiError } from '@/configs/axios.config'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Typography } from '@/components/ui/typography'
import { CompanyDetailLayout } from '@/components/custom/companies/overview/company-detail-layout'
import {
  PortalTabs,
  isPortalTabValue,
  type PortalTabValue,
} from '@/components/custom/portal/portal-tabs'
import { PortalOverviewTab } from '@/components/custom/portal/portal-overview-tab'
import { PortalFinancialsTab } from '@/components/custom/portal/portal-financials-tab'
import { PortalTeamTab } from '@/components/custom/portal/portal-team-tab'
import { PortalOgsmTab } from '@/components/custom/portal/portal-ogsm-tab'
import type { OgsmNavigation } from '@/components/custom/ogsm/ogsm-workspace'
import {
  PortalSidePanel,
  PortalSidePanelSheet,
  PORTAL_SIDE_PANEL_WIDTH,
} from '@/components/custom/portal/portal-side-panel'
import { parseOgsmSearch } from '@/features/ogsm/search'
import { usePortalCompany } from '@/hooks/use-portal'
import { usePortalOgsmAccess } from '@/hooks/use-portal-ogsm'
import { useRole } from '@/hooks/use-permissions'
import { formatEnumValue } from '@/lib/formatters'
import { cn } from '@/lib/utils'
import type { PortalCompany } from '@/services/portal.service'

type PortalSearch = {
  tab?: PortalTabValue
  trailingPeriod?: 'LTM' | 'MONTHLY' | 'QUARTERLY' | 'YTD'
  view?: 'MONTHLY' | 'QUARTERLY' | 'ANNUAL'
  /** OGSM tab: UUIDs only (`parseOgsmSearch`). */
  session?: string
  objective?: string
}

const VALID_TRAILING_PERIODS = ['LTM', 'MONTHLY', 'QUARTERLY', 'YTD'] as const
const VALID_VIEWS = ['MONTHLY', 'QUARTERLY', 'ANNUAL'] as const

export const Route = createFileRoute('/portal/')({
  component: PortalCompanyPage,
  validateSearch: (search: Record<string, unknown>): PortalSearch => ({
    tab:
      typeof search.tab === 'string' && isPortalTabValue(search.tab)
        ? search.tab
        : undefined,
    trailingPeriod:
      typeof search.trailingPeriod === 'string' &&
      (VALID_TRAILING_PERIODS as readonly string[]).includes(
        search.trailingPeriod
      )
        ? (search.trailingPeriod as PortalSearch['trailingPeriod'])
        : undefined,
    view:
      typeof search.view === 'string' &&
      (VALID_VIEWS as readonly string[]).includes(search.view)
        ? (search.view as PortalSearch['view'])
        : undefined,
    ...parseOgsmSearch(search),
  }),
})

function PortalCompanyPage() {
  const { tab, trailingPeriod, view, session, objective } = Route.useSearch()
  const navigate = Route.useNavigate()

  const {
    data: company,
    isLoading,
    isError,
    error,
    refetch,
  } = usePortalCompany()
  const role = useRole()
  const isPortcoAdmin = role === 'PORTCO_ADMIN'
  const { canView: canViewOgsm } = usePortalOgsmAccess()
  const activeTab =
    (tab === 'management' && !isPortcoAdmin) || (tab === 'ogsm' && !canViewOgsm)
      ? 'overview'
      : (tab ?? 'overview')

  const handleTabChange = (next: PortalTabValue) => {
    navigate({
      search: (prev) => ({
        ...prev,
        tab: next === 'overview' ? undefined : next,
        session: undefined,
        objective: undefined,
      }),
    })
  }

  const handleOgsmNavigate = (next: OgsmNavigation) => {
    navigate({ search: (prev) => ({ ...prev, ...next }) })
  }

  const handleNavigateToFinancials = (
    nextTab: PortalTabValue,
    nextTrailingPeriod?: string,
    nextView?: string
  ) => {
    navigate({
      search: (prev) => ({
        ...prev,
        tab: nextTab === 'overview' ? undefined : nextTab,
        trailingPeriod: nextTrailingPeriod as PortalSearch['trailingPeriod'],
        view: nextView as PortalSearch['view'],
      }),
    })
  }

  // The 360px panel crushes the six-column board below ~1350px; the details sheet covers it.
  const showSidePanel = activeTab !== 'ogsm'

  if (isLoading) {
    return (
      <CompanyDetailLayout
        sidePanel={
          showSidePanel ? (
            <div className="hidden h-full lg:block">
              <PortalSidePanelSkeleton />
            </div>
          ) : undefined
        }
      >
        <div className="space-y-4 px-3 pb-8 pt-12 sm:px-4">
          <Skeleton className="h-5 w-60" />
          <Skeleton className="h-12 w-80" />
          <Skeleton className="h-10 w-full" />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton key={index} className="h-56 rounded-sm" />
            ))}
          </div>
        </div>
      </CompanyDetailLayout>
    )
  }

  if (isError || !company) {
    // 404 here means the account has no active company assignment — retrying
    // will never help, so point the user at their Greybull contact instead.
    const isUnassigned = getApiError(error).statusCode === 404

    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex max-w-100 flex-col items-center gap-3 rounded-lg border border-primary/15 bg-white px-8 py-10 text-center">
          <AlertCircle className="h-5 w-5 text-destructive" />
          <Typography variant="h5">
            {isUnassigned
              ? 'No company assigned to your account'
              : 'Failed to load company details'}
          </Typography>
          {isUnassigned ? (
            <Typography variant="muted">
              Contact your Greybull Stewardship representative to get access.
            </Typography>
          ) : (
            <Button variant="outline" onClick={() => refetch()}>
              Retry
            </Button>
          )}
        </div>
      </div>
    )
  }

  return (
    <CompanyDetailLayout
      sidePanel={
        showSidePanel ? (
          <div className="hidden h-full lg:block">
            <PortalSidePanel company={company} />
          </div>
        ) : undefined
      }
    >
      <div className="space-y-4 px-3 pb-8 pt-12 sm:px-4">
        <div className="border-b border-border pb-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <Typography variant="h1" className="break-words">
                {company.displayName}
              </Typography>
              <Badge variant={company.status === 'ACTIVE' ? 'green' : 'red'}>
                {formatEnumValue(company.status)}
              </Badge>
            </div>
            <PortalDetailsButton
              company={company}
              hasSidePanel={showSidePanel}
            />
          </div>
        </div>

        <PortalTabs
          value={activeTab}
          onValueChange={handleTabChange}
          showTeam={isPortcoAdmin}
          showOgsm={canViewOgsm}
        />

        <div
          id="portal-tabpanel"
          role="tabpanel"
          aria-labelledby={`portal-tab-${activeTab}`}
          tabIndex={0}
        >
          {activeTab === 'overview' ? (
            <PortalOverviewTab
              onNavigateToFinancials={handleNavigateToFinancials}
            />
          ) : activeTab === 'management' && isPortcoAdmin ? (
            <PortalTeamTab />
          ) : activeTab === 'ogsm' && canViewOgsm ? (
            <PortalOgsmTab
              key={company.id}
              companyId={company.id}
              companyName={company.displayName}
              sessionId={session}
              objectiveId={objective}
              onNavigate={handleOgsmNavigate}
            />
          ) : (
            <PortalFinancialsTab
              initialTrailingPeriod={trailingPeriod}
              initialView={view}
              onNavigateToFinancials={handleNavigateToFinancials}
            />
          )}
        </div>
      </div>
    </CompanyDetailLayout>
  )
}

function PortalDetailsButton({
  company,
  hasSidePanel,
}: {
  company: PortalCompany
  /** Without the panel the button is the only way to the details, at every width. */
  hasSidePanel: boolean
}) {
  const [isDetailsOpen, setIsDetailsOpen] = useState(false)

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setIsDetailsOpen(true)}
        className={cn(
          'h-9 shrink-0 rounded-md border-primary/20 bg-white px-2.5 text-xs font-medium text-primary hover:bg-primary/5',
          hasSidePanel && 'lg:hidden'
        )}
      >
        <PanelRightOpen className="size-3.5" />
        Company details
      </Button>
      <PortalSidePanelSheet
        open={isDetailsOpen}
        onOpenChange={setIsDetailsOpen}
        company={company}
      />
    </>
  )
}

function PortalSidePanelSkeleton() {
  return (
    <div
      className={cn(
        PORTAL_SIDE_PANEL_WIDTH,
        'h-full shrink-0 border-l border-primary/50 bg-sidebar px-3 py-6'
      )}
    >
      <div className="space-y-3">
        <Skeleton className="h-32 w-full rounded-sm" />
        <Skeleton className="h-36 w-full rounded-sm" />
      </div>
    </div>
  )
}
